import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { BookOpen, ExternalLink, X } from "lucide-react";
import api from "../lib/api";

/**
 * Global "About this on Wikipedia" card. Auto-mounts on content pages and
 * infers its query from the page's first <h1> (falls back to document.title).
 * Renders nothing if Wikipedia has no matching (or only disambiguation) page.
 *
 * Cached server-side for 30 days (see backend/wiki_summary.py), and dismissible
 * per-page in localStorage so users don't see the same card again after
 * they've closed it.
 */
const HIDE_ON = [
  "/admin", "/auth", "/app-login", "/auth-complete", "/delete-account",
  "/embed", "/widgets", "/travel-day", "/quote/", "/dashboard",
  "/expenses", "/trip-expenses/shared", "/my-social-pulse", "/locker",
  "/family-expense-hub", "/documents-locker", "/moderation", "/contact",
  "/legal/", "/faq",
];

const DISMISS_KEY = "tp_wiki_card_dismissed";

function readDismissed() {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}"); } catch { return {}; }
}

function inferQuery() {
  const h1 = document.querySelector("main h1");
  const raw = (h1?.innerText || document.title || "").trim();
  // Drop obvious app-name suffixes like " — TravelerPulse.pro"
  return raw.split(/[|·—–]/)[0].trim();
}

export default function WikipediaCard() {
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (HIDE_ON.some((p) => pathname.startsWith(p))) { setData(null); return; }
    let cancelled = false;

    // Wait for the h1 to render (data-fed pages populate late)
    const tryFetch = async (attempt = 0) => {
      if (cancelled) return;
      const q = inferQuery();
      if (!q || q.length < 3) {
        if (attempt < 6) return setTimeout(() => tryFetch(attempt + 1), 500);
        return;
      }
      const map = readDismissed();
      const key = `${pathname}::${q}`;
      if (map[key]) { setDismissed(true); return; }

      setQuery(q);
      try {
        const { data: d } = await api.get("/wiki/summary", { params: { q } });
        if (!cancelled && d?.found && d.snippet) setData({ ...d, dismissKey: key });
      } catch { /* silent — the card is opt-in */ }
    };

    setData(null);
    setDismissed(false);
    const t = setTimeout(() => tryFetch(0), 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pathname]);

  const dismiss = () => {
    if (!data) return;
    const map = readDismissed();
    map[data.dismissKey] = 1;
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(map)); } catch { /* full */ }
    setDismissed(true);
  };

  if (!data || dismissed) return null;

  return (
    <div className="max-w-3xl mx-auto px-5 my-8 print:hidden" data-testid="wikipedia-card">
      <div className="border border-zinc-300 bg-white flex gap-4 p-4 sm:p-5">
        {data.thumbnail && (
          <img
            src={data.thumbnail}
            alt=""
            loading="lazy"
            className="w-20 h-20 sm:w-24 sm:h-24 object-cover border border-zinc-200 shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> About on Wikipedia
              </div>
              <h3 className="font-heading font-700 text-lg text-zinc-900 mt-0.5 truncate" title={data.title}>
                {data.title}
              </h3>
            </div>
            <button
              onClick={dismiss}
              data-testid="wiki-card-dismiss"
              aria-label="Hide this Wikipedia card"
              className="text-zinc-400 hover:text-zinc-700 -mr-1 -mt-1 shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {data.description && (
            <div className="text-xs text-zinc-500 italic mb-1">{data.description}</div>
          )}
          <p className="text-sm text-zinc-700 leading-relaxed">{data.snippet}</p>
          <a
            href={data.url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="wiki-card-link"
            className="mt-2 inline-flex items-center gap-1 text-sm font-700 text-[#0e7490] hover:underline"
          >
            Read on Wikipedia <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <p className="text-[10px] text-zinc-400 mt-1">
            Text from Wikipedia, licensed under CC BY-SA. May be out of date — verify important details.
          </p>
        </div>
      </div>
    </div>
  );
}
